import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Box,
  Flex,
  Input,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { FaUser } from 'react-icons/fa';
import { RiAddLine, RiCheckLine, RiCloseLine } from 'react-icons/ri';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  useDigitalTwinsQuery,
  useAddDigitalTwinToCollectionMutation,
  useRemoveDigitalTwinFromCollectionMutation,
} from '../../generated/graphql';

interface AddPersonasToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionName: string;
  // Persona ids already in the collection.
  existingTwinIds: Set<string>;
  onSuccess: () => void;
}

export const AddPersonasToCollectionModal = ({
  isOpen,
  onClose,
  collectionId,
  collectionName,
  existingTwinIds,
  onSuccess,
}: AddPersonasToCollectionModalProps) => {
  const toast = useToast();
  const [search, setSearch] = useState('');
  // Locally-tracked add/remove so rows flip immediately without a refetch.
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, loading } = useDigitalTwinsQuery({
    variables: { limit: 100, offset: 0, search },
    skip: !isOpen,
  });
  const [addDigitalTwinToCollection] = useAddDigitalTwinToCollectionMutation();
  const [removeDigitalTwinFromCollection] = useRemoveDigitalTwinFromCollectionMutation();

  const twins = data?.getAllDigitalTwins?.twins ?? [];

  const isInCollection = useMemo(
    () => (id: string) =>
      (existingTwinIds.has(id) || addedIds.has(id)) && !removedIds.has(id),
    [existingTwinIds, addedIds, removedIds]
  );

  const handleAdd = async (digitalTwinId: string, name: string) => {
    setBusyId(digitalTwinId);
    try {
      await addDigitalTwinToCollection({ variables: { collectionId, digitalTwinId } });
      setAddedIds((prev) => new Set(prev).add(digitalTwinId));
      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.delete(digitalTwinId);
        return next;
      });
      onSuccess();
      toast({ title: `Added "${name}"`, status: 'success', duration: 2000 });
    } catch (error) {
      toast({
        title: 'Could not add persona',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (digitalTwinId: string, name: string) => {
    setBusyId(digitalTwinId);
    try {
      await removeDigitalTwinFromCollection({ variables: { collectionId, digitalTwinId } });
      setRemovedIds((prev) => new Set(prev).add(digitalTwinId));
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(digitalTwinId);
        return next;
      });
      onSuccess();
      toast({ title: `Removed "${name}"`, status: 'success', duration: 2000 });
    } catch (error) {
      toast({
        title: 'Could not remove persona',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HelivanText variant="subheader">Add personas</HelivanText>
          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
            to "{collectionName}"
          </HelivanText>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input
            placeholder="Search personas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            mb={3}
            autoFocus
          />
          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner color={HelivanColor.text.helivanBlue} />
            </Flex>
          ) : twins.length === 0 ? (
            <Box textAlign="center" py={8}>
              <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                No personas found.
              </HelivanText>
            </Box>
          ) : (
            <VStack spacing={2} align="stretch">
              {twins.map((twin) => {
                const inCollection = isInCollection(twin.id);
                return (
                  <Flex
                    key={twin.id}
                    p={3}
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    borderRadius="10px"
                    justify="space-between"
                    align="center"
                  >
                    <HStack spacing={3} minW={0}>
                      <Box
                        w="32px"
                        h="32px"
                        borderRadius="full"
                        bg={HelivanColor.text.helivanBlue}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <FaUser size={14} color="white" />
                      </Box>
                      <Box minW={0}>
                        <HelivanText variant="smallBody" style={{ fontWeight: 600 }}>
                          {twin.name}
                        </HelivanText>
                        {twin.type && (
                          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                            {twin.type}
                          </HelivanText>
                        )}
                      </Box>
                    </HStack>
                    {inCollection ? (
                      <HStack spacing={3}>
                        <HStack spacing={1} color={HelivanColor.text.helivanBlue}>
                          <RiCheckLine />
                          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.helivanBlue}>
                            Added
                          </HelivanText>
                        </HStack>
                        <HelivanButton
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(twin.id, twin.name)}
                          isLoading={busyId === twin.id}
                        >
                          <HStack spacing={1} color="#E53E3E">
                            <RiCloseLine size={14} />
                            <span>Remove</span>
                          </HStack>
                        </HelivanButton>
                      </HStack>
                    ) : (
                      <HelivanButton
                        variant="outline"
                        size="sm"
                        onClick={() => handleAdd(twin.id, twin.name)}
                        isLoading={busyId === twin.id}
                      >
                        <HStack spacing={1}>
                          <RiAddLine size={14} />
                          <span>Add</span>
                        </HStack>
                      </HelivanButton>
                    )}
                  </Flex>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HelivanButton variant="outline" size="sm" onClick={onClose}>
            Done
          </HelivanButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
